import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { observer } from "mobx-react-lite";
import organizationStore from "../../stores/OrganizationStore";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import CustomDataGridPro from "../CustomDataGridPro";

const OrganizationComponent = observer(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [newOrganization, setNewOrganization] = useState({
    name: "",
    type: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    organizationStore.fetchOrganizations();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewOrganization((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditOpen = (organization) => {
    setIsEditMode(true);
    setCurrentOrganization(organization);
    setNewOrganization({ name: organization.name, type: organization.type });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (isEditMode && currentOrganization) {
      await organizationStore.updateOrganization(
        currentOrganization.id,
        newOrganization
      );
    } else {
      await organizationStore.createOrganization(newOrganization);
    }

    setIsModalOpen(false);
    setIsEditMode(false);
    setNewOrganization({ name: "", type: "" });
  };

  const handleDelete = async (id) => {
    await organizationStore.deleteOrganization(id);
  };

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "name",
        headerName: "Organization Name",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "type",
        headerName: "Type",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160, // fixed so buttons don't stretch
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <>
            <IconButton
              onClick={() => handleEditOpen(params.row)}
              sx={{
                color: "#22C55E",
                mr: 1,
                "&:hover": { backgroundColor: "#BBF7D0" },
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDelete(params.row.id)}
              sx={{
                color: "#6B7280",
                "&:hover": { backgroundColor: "#E5E7EB" },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </>
        ),
      },
    ],
    [handleEditOpen, handleDelete]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <BusinessIcon fontSize="large" sx={{ mr: 2, color: "#22C55E" }} />
            <Typography variant="h4" fontWeight="bold">
              Organizations Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your organizations from this dashboard
          </Typography>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setIsEditMode(false);
            setNewOrganization({ name: "", type: "" });
            setIsModalOpen(true);
          }}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
        >
          Add Organization
        </Button>
      </Box>

      <CustomDataGridPro
        rows={organizationStore.organizations.slice()}
        columns={columns}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={organizationStore.organizations.length}
      />

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>
          {isEditMode ? "Edit Organization" : "Add New Organization"}
        </DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Organization Name"
            fullWidth
            margin="normal"
            value={newOrganization.name}
            onChange={handleChange}
          />
          <TextField
            name="type"
            label="Type"
            fullWidth
            margin="normal"
            value={newOrganization.type}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{
              textTransform: "uppercase",
              color: "#000",
              borderColor: "#000",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderColor: "#000",
              },
            }}
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#22C55E",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#16A34A" },
            }}
          >
            {isEditMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default OrganizationComponent;
